import { useState } from 'react';
import type { HandleListItem } from '@pomoshnik/shared';

/**
 * Сетка карточек товаров (1 колонка). Презентационный компонент.
 *
 * Ф-Фронт (ADR-0070): реальное фото из R2 (signed-URL по `primaryPhotoKey`) в
 * верхней зоне карточки; нет фото / onError → плейсхолдер (инициал бренда / «?»
 * для магазин-заглушек с пустым brand). Паритет с `DecorResultCard`.
 */
export interface ResultCardsProps {
  items: readonly HandleListItem[];
  /** Ф-Фронт: map `photoKey → signed-URL` (из useHandleImageUrls). Отсутствие
   *  ключа = ещё грузится / ошибка / нет фото → плейсхолдер. */
  imageUrls?: Record<string, string>;
  onItemClick: (globalId: string) => void;
}

export function ResultCards({ items, imageUrls, onItemClick }: ResultCardsProps) {
  return (
    // Адаптивная сетка (full-bleed): auto-fill без хардкод-брейкпоинтов —
    // мобайл (~360px) → 1 колонка, ~480px → 2, десктоп заполняет всю ширину
    // (4–7+ колонок). Карточки aspect-square + object-contain ровно тайлятся.
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
      {items.map((item) => (
        <HandleGridCard
          key={item.globalId}
          item={item}
          imageUrl={item.primaryPhotoKey ? imageUrls?.[item.primaryPhotoKey] : undefined}
          onItemClick={onItemClick}
        />
      ))}
    </div>
  );
}

function HandleGridCard({
  item,
  imageUrl,
  onItemClick,
}: {
  item: HandleListItem;
  imageUrl?: string;
  onItemClick: (globalId: string) => void;
}) {
  // Ф-Фронт: при ошибке загрузки <img> откатываемся на плейсхолдер.
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imgFailed;
  const hasBrand = item.brand.trim() !== '';
  // Плейсхолдер: инициал бренда; у магазин-заглушек (пустой brand) — «?».
  const placeholderChar = hasBrand ? item.brand.charAt(0) : '?';

  return (
    <button
      type="button"
      onClick={() => onItemClick(item.globalId)}
      aria-label={`Открыть карточку: ${item.name}, ${item.brand}`}
      className={[
        'w-full rounded-xl bg-card border-[1.5px] border-accent shadow-card overflow-hidden',
        'text-left p-0 cursor-pointer',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      <div
        className="w-full aspect-square bg-card flex items-center justify-center text-muted text-3xl font-display overflow-hidden"
        aria-hidden="true"
      >
        {showImage ? (
          // alt="" — декоративно: имя/бренд уже в aria-label кнопки.
          // object-contain: товар целиком (не обрезан кропом), letterbox на белом
          // bg-card — хорошо смотрится на любой ширине (desktop-mobile-frame).
          <img
            src={imageUrl as string}
            alt=""
            className="w-full h-full object-contain"
            onError={() => setImgFailed(true)}
          />
        ) : (
          placeholderChar
        )}
      </div>
      <div className="p-5">
        <p className="font-body text-[18px] font-semibold text-ink m-0 line-clamp-2">{item.name}</p>
        {/* Пустой brand (магазин-заглушка) — строку бренда скрываем (ADR-0070 §4). */}
        {hasBrand && <p className="text-muted text-base mt-1 mb-0">{item.brand}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge>{item.color}</Badge>
          {item.shape && <Badge>{item.shape}</Badge>}
        </div>
      </div>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-sm px-3 py-1 rounded-full bg-icon-bg text-separator">{children}</span>
  );
}
